import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductForm from './productForm';
import { initValueProduct } from '../../shared/initials';
import { getGridDataByModuleKey } from '../../redux/selector';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { validationSchemaProduct } from './productValidationSchema';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import { productReferenceLookup } from '../../shared/constants';
import Loader from '../../shared/component/Loader';

const CL_ICONS = {
  chevL: <path d="M15 18l-6-6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
};
const CLIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {CL_ICONS[name] || null}
  </svg>
);

export const AddProduct = () => {
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(
    stateData,
    'revenueTypeList',
    'leadSourceList',
    'industryList',
    'timeZone'
  );
  const location = useLocation();
  const [formData, setFormData] = useState(initValueProduct);
  const [errormsg, setErrorMsg] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loading, setLoading] = useState(storeLoading);
  const navigate = useNavigate();
  const gridData = useSelector(getGridDataByModuleKey('productList'));
  const [imagefile, setImageFile] = useState(null);
  const [formEdited, setFormEdited] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('createProduct') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editProduct') && location.state) {
      updatedFormData = {
        ...location.state,
        type: 'edit',
      };
    }
    setFormData(updatedFormData);
  }, [location]);

  const handleCloseAddForm = () => {
    if (formData.from === 'view') {
      navigate(`/products/productList/viewProduct?id=${formData.id}`);
    } else {
      navigate('/products/productList');
    }
  };

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    let convertedValue = value;

    // For numeric-only fields
    if (
      [
        'quantityInDemand',
        'quantityOrdered',
        'quantityInStock',
        'reorderLevel',
        'commissionRate',
      ].includes(name)
    ) {
      convertedValue = value.replace(/[^0-9]/g, '');
    }

    // For unitPrice
    if (['unitPrice'].includes(name)) {
      if (value === '') {
        convertedValue = null; // Send null instead of empty string
      } else {
        let sanitized = value.replace(/[^0-9.]/g, '');

        const parts = sanitized.split('.');
        if (parts.length > 2) {
          sanitized = parts[0] + '.' + parts[1];
        }

        if (parts.length === 2) {
          parts[1] = parts[1].slice(0, 2);
          sanitized = parts[0] + '.' + parts[1];
        }

        const parsed = parseFloat(sanitized);
        convertedValue = !isNaN(parsed) && /^\d+(\.\d{1,2})?$/.test(sanitized) ? parsed : sanitized;
      }
    }

    // Clear the error for this field
    setErrorMsg((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: convertedValue,
    }));

    setFormEdited(true);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    const isNew = selectedOption.__isNew__;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));

    if (name === 'priceModelId') {
      setFormData({
        ...formData,
        priceModelId: selectedId,
        priceModel: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'productOwnerId') {
      setFormData({
        ...formData,
        productOwnerId: selectedId,
        productOwner: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'usageUnitId') {
      setFormData({
        ...formData,
        usageUnitId: selectedId,
        usageUnit: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'handlerId') {
      setFormData({
        ...formData,
        handlerId: selectedId,
        handler: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'handlerId') {
      setFormData({
        ...formData,
        handlerId: selectedId,
        handler: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'taxId') {
      setFormData({
        ...formData,
        taxId: selectedId,
        tax: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'vendorId') {
      setFormData((prev) => ({
        ...prev,
        vendorName: selectedValue,
        ...(isNew ? {} : { vendorId: selectedId }), // Only include ID if not new
      }));
    } else if (name === 'manufacturerId') {
      setFormData((prev) => ({
        ...prev,
        manufacturer: selectedValue,
        ...(isNew ? {} : { manufacturerId: selectedId }),
      }));
    } else if (name === 'productCategoryId') {
      setFormData((prev) => ({
        ...prev,
        productCategory: selectedValue,
        ...(isNew ? {} : { productCategoryId: selectedId }),
      }));
    }
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target; // Use 'checked' for true/false value
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked, // Set the checkbox state as true/false
    }));
    setFormEdited(true);
  };

  const updateList = async (msg, id) => {
    setLoading(false);

    toast.success(msg, {
      position: 'top-right',
    });

    const columnName = 'prodcutName';
    const orderType = 'desc';
    const data = {
      pageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE),
      pageNumber: 1,
      columnName,
      orderType,
      filterJson: null,
    };

    try {
      // ✅ Await the dispatch before navigation
      // await dispatch(
      //   fetchDataAndUpdateStore({ key: "productList", inputData: data })
      // );
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'Product',
          moduleKey: 'productList',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      dispatch(
        fetchDataAndUpdateStore({
          key: 'productReferenceLookup',
          inputData: productReferenceLookup,
        })
      );
      if (formData.from === 'view') {
        navigate(`/products/productList/viewProduct?id=${formData.id}`);
      } else {
        navigate('/products/productList');
      }
    } catch (error) {
      toast.error('Something went wrong while updating the list.', {
        position: 'top-right',
      });
    }
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const sanitizedData = {
        ...formData,
      };

      await validationSchemaProduct.validate(sanitizedData, {
        abortEarly: false,
      });
      handleFormSubmit(sanitizedData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error.inner.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
    }
  };

  const handleFormSubmit = (formData) => {
    if (formData.type === 'add') {
      apiServices
        .createProduct(formData)
        .then((response) => {
          if (response.status === 200) {
            const id = response?.data;
            updateList('Product created successfully!', id);
            setLoading(false);
            handleCloseAddForm();
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);

          const formatFieldNames = (msg) => msg.replace(/([A-Z])/g, ' $1').trim(); // turns SaleEndDate -> Sale End Date

          if (error.response?.data?.errors) {
            const errors = error.response.data.errors;

            Object.values(errors).forEach((messages) => {
              messages.forEach((msg) => {
                toast.error(formatFieldNames(msg), { position: 'top-right' });
              });
            });
          } else {
            const fallbackMsg =
              error.response?.data?.title || error.message || 'An unexpected error occurred.';
            toast.error(fallbackMsg, { position: 'top-right' });
          }

          setLoading(false);
        });
    } else {
      apiServices
        .updateProduct(formData)
        .then((response) => {
          if (response.status === 200) {
            updateList('Product updated successfully!');
            setLoading(false);
            handleCloseAddForm();
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);

          const formatFieldNames = (msg) => msg.replace(/([A-Z])/g, ' $1').trim(); // turns SaleEndDate -> Sale End Date

          if (error.response?.data?.errors) {
            const errors = error.response.data.errors;

            Object.values(errors).forEach((messages) => {
              messages.forEach((msg) => {
                toast.error(formatFieldNames(msg), { position: 'top-right' });
              });
            });
          } else {
            const fallbackMsg =
              error.response?.data?.title || error.message || 'An unexpected error occurred.';
            toast.error(fallbackMsg, { position: 'top-right' });
          }

          setLoading(false);
        });
    }
  };

  const isEdit = formData?.type === 'edit';
  const canSave = !!(formData?.name && String(formData.name).trim());

  return (
    <div className="gos-cl gos-quoteform">
      {loading && <Loader />}
      {/* sticky header */}
      <div className="gos-cl-header">
        <button type="button" className="gos-cl-back" onClick={handleCloseAddForm} title="Back to Products">
          <CLIc name="chevL" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-htitle">{isEdit ? 'Edit Product' : 'Create Product'}</div>
          <div className="gos-cl-hsub">
            Fields marked <span className="gos-cl-reqdot">*</span> are required
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
        <button
          type="button"
          className="gos-btn gos-btn-primary"
          onClick={() => handleSubmit(formData)}
          disabled={loading || (isEdit && !formEdited) || !canSave}
          style={{ opacity: canSave && !(isEdit && !formEdited) ? 1 : 0.55 }}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving…
            </>
          ) : (
            <>
              <CLIc name="check" size={16} /> Save Product
            </>
          )}
        </button>
      </div>

      <div className="gos-cl-scroll">
        <ProductForm
          formData={formData}
          handleChangeForm={handleChangeForm}
          handleChangeCheckbox={handleChangeCheckbox}
          handleChange={handleChange}
          errormsg={errormsg}
        />
      </div>
    </div>
  );
};

export default AddProduct;
